<template>
  <div class="trainer-tab">
    <div class="row">
      <div class="col text-end">
        <Button v-if="allowedToEdit" :label="t('ADD')" :disabled="isArchived" @click="toggleSearchForm"/>
      </div>
    </div>

    <div class="row" v-if="addTrainerModal">
      <div class="col">
        <form>
          <div class="mb-2">
            <label for="search-trainers" class="form-label">{{ t('PERSON_SEARCH_TIP') }}</label>
            <SearchInput input-id="search-trainers" :on-input="searchTrainer" @on-change="submitTrainer"
                         :confirm-on-select-text="t('ADD_TRAINER')" :required-symbols="3"/>
          </div>
        </form>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <DataTable
            :value="props.trainers"
            striped-rows
            paginator
            size="small"
            :rows="perPage"
            :rowHover="true"
            :alwaysShowPaginator="false"
            :removableSort="true"
        >
          <template #empty>{{ t('NO_RESULTS') }}</template>

          <Column
              v-for="f in trainerFields"
              :field="f.key"
              :header="f.label"
              :sortable="f.sortable !== false"
          />

          <Column key="action">
            <template #body="{ data }">
              <div class="text-center">
                <Button v-if="allowedToEdit" :disabled="isArchived" icon="pi pi-times" text rounded
                        @click="emit('removeTrainer', data.id)" severity="danger" v-tooltip="t('REMOVE')"/>
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {ref} from "vue";
import SearchInput from "@/components/SearchInput.vue";
import {injectAxios} from "@/util/inject";
import type {AccountDto} from "@/model/account";
import {useRoute} from "vue-router";

const props = defineProps<{
  tabTitle: string;
  trainers: object[];
  isArchived: boolean;
  trainerFields: object[];
  allowedToEdit: boolean;
  trainingId?: string;
}>();

const emit = defineEmits<{
  (e: 'submitTrainer', trainer: { value: string, label: string }): void
  (e: 'removeTrainer', id: string): void
}>()

const axios = injectAxios();
const route = useRoute();

const perPage = 15;
const addTrainerModal = ref(false);

const {t} = useI18n();

const searchTrainer = async (searchValue: string) => {
  const searchResults = props.trainingId
      ? await getModuleTrainers(searchValue, props.trainingId)
      : await getTrainers(searchValue);

  const existingTrainerIds = new Set((props.trainers || []).map((t) => (t as any).id));
  return searchResults
      .filter((t: AccountDto) => !t.isArchived && !existingTrainerIds.has(t.id))
      .map((t: AccountDto) => ({
        value: t.id,
        label: `${t.fullName} (${t.ssn})`,
      }));
};


const getModuleTrainers = async (searchValue: string, trainingId: string) => {
  const params = {
    searchValue: searchValue,
  };

  const response = await axios.get(`/training/${trainingId}/group/${route.params.id}/school_trainers/search`, {
    params: params
  });

  return response.data || [];
};

const getTrainers = async (searchValue: string) => {
  const params = {
    fields: "SSN,NAME",
    searchValue: searchValue,
  };

  const response = await axios.get(`/account/search/trainers`, {
    params: params
  });

  return response.data.content || [];
};

const submitTrainer = async (trainer: { value: string, label: string } | null) => {
  if (!trainer) {
    return;
  }

  emit("submitTrainer", trainer);
  toggleSearchForm();
}

const toggleSearchForm = () => {
  addTrainerModal.value = !addTrainerModal.value;
};
</script>
